import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiPlay } from "react-icons/fi";
import { trackVideo, type VideoEvent } from "../../lib/analytics";
import { Spinner } from "../ui";
import videoSrc from "../../assets/explainer/rolelogic-explainer.mp4";
import posterSrc from "../../assets/explainer/rolelogic-explainer-poster.webp";
// ?no-inline: a caption file this small would otherwise be inlined as a data:
// URL, which <track> will not load.
import captionsSrc from "../../assets/explainer/rolelogic-explainer.en.vtt?url&no-inline";

/*
 * The explainer tour, rendered by promo/ (the PromoExplainerWide
 * composition — see promo/README.md for how to re-cut it and re-encode these
 * three files). The video speaks English and burns its headlines in, so the
 * captions track is English too, whatever the page's locale.
 */

/** The analytics title: a fixed name, so reports do not split by locale. */
const VIDEO_TITLE = "RoleLogic explainer";
/** Shown on the poster. The file is 100.8s. */
const DURATION_LABEL = "1:41";
/** GA4's enhanced-measurement milestones. */
const MILESTONES = [10, 25, 50, 75];

/**
 * Where the end card draws its "Get Started" button, in the video's own
 * 1920×1080 pixels — measured from the render, not derived from the layout:
 * 620×132 at (650, 594), with the 28px corners of the site's `lg` button
 * (promo/src/explainer/wide/CtaCard.tsx). The page lays its real button
 * exactly over it, so the button the viewer watches being pressed is the one
 * they press. Re-measure if that card changes.
 */
const FRAME = { w: 1920, h: 1080 } as const;
const CTA = { x: 650, y: 594, w: 620, h: 132, r: 28 } as const;
/** The card starts at 95.0s; its button springs in and has settled by here. */
const CTA_FROM = 96.6;

const ctaBox: React.CSSProperties = {
  left: `${(CTA.x / FRAME.w) * 100}%`,
  top: `${(CTA.y / FRAME.h) * 100}%`,
  width: `${(CTA.w / FRAME.w) * 100}%`,
  height: `${(CTA.h / FRAME.h) * 100}%`,
  // Percentage radii are per axis, so the corner is stated against each.
  borderRadius: `${(CTA.r / CTA.w) * 100}% / ${(CTA.r / CTA.h) * 100}%`,
};

export const ExplainerVideo: React.FC<{
  startLabel: string;
  loading: boolean;
  onStart: () => void;
}> = ({ startLabel, loading, onStart }) => {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [started, setStarted] = useState(false);
  const [ended, setEnded] = useState(false);
  // The end card's button is on screen, so the real one is laid over it.
  const [onCta, setOnCta] = useState(false);
  // Milestones already reported for this page view, so replays and seeking
  // back do not count a viewer twice.
  const reported = useRef(new Set<string>());

  const report = useCallback((event: VideoEvent, percent: number) => {
    const key = `${event}:${percent}`;
    const video = videoRef.current;
    if (!video || reported.current.has(key)) return;
    reported.current.add(key);
    trackVideo(event, {
      video_title: VIDEO_TITLE,
      video_duration: Math.round(video.duration || 0),
      video_current_time: Math.round(video.currentTime),
      video_percent: percent,
    });
  }, []);

  // Nothing is downloaded until this click: preload="none" keeps a 72-second
  // file off the critical path of a page most visitors only scroll past.
  const play = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    setStarted(true);
    video.play().catch(() => {
      // Blocked or interrupted: the native controls are showing now, so the
      // viewer can start it themselves.
    });
  }, []);

  // The play button unmounts on click; hand focus to the video once it has
  // its controls, so a keyboard viewer is not dropped back to the page body.
  useEffect(() => {
    if (started) videoRef.current?.focus({ preventScroll: true });
  }, [started]);

  const onTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    // Seeking back or replaying takes it away again.
    setOnCta(video.currentTime >= CTA_FROM);
    if (!video.duration) return;
    const percent = (video.currentTime / video.duration) * 100;
    for (const mark of MILESTONES) {
      if (percent >= mark) report("video_progress", mark);
    }
  }, [report]);

  return (
    <section
      aria-labelledby="explainer-title"
      // overflow-x-clip: the glow reaches past the frame, and on a narrow
      // screen past the page edge, where it would add a sideways scroll.
      className="relative mx-auto max-w-6xl overflow-x-clip px-4 py-16"
    >
      <div className="mb-10 text-center">
        <p className="mb-4 font-mono text-xs uppercase tracking-[0.22em] text-indigo-300">
          {t("HomePage.videoEyebrow")}
        </p>
        <h2
          id="explainer-title"
          className="mb-4 text-2xl font-bold text-white sm:text-4xl"
        >
          {t("HomePage.videoTitle")}
        </h2>
        <p className="mx-auto max-w-2xl text-lg text-zinc-300">
          {t("HomePage.videoSubtitle")}
        </p>
      </div>

      <div className="relative mx-auto max-w-5xl">
        {/* glow behind the frame, the page's own indigo */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -inset-6 rounded-[2rem] bg-[radial-gradient(ellipse_at_center,rgba(99,102,241,0.18),transparent_70%)]"
        />
        <div className="relative aspect-video overflow-hidden rounded-2xl border border-white/10 bg-gray-950 shadow-2xl shadow-black/50 ring-1 ring-indigo-400/10">
          {/* No playsInline: on an iPhone a 16:9 frame inline is 360px wide,
              and the canvas in it is built to be read larger than that.
              iOS opens it full screen in landscape instead. */}
          <video
            ref={videoRef}
            className="absolute inset-0 h-full w-full"
            poster={posterSrc}
            preload="none"
            controls={started}
            onPlay={() => setEnded(false)}
            // "playing", not "play": with preload="none" the first play
            // fires before the metadata, when the duration is still 0.
            onPlaying={() => report("video_start", 0)}
            onTimeUpdate={onTimeUpdate}
            onEnded={() => {
              setEnded(true);
              report("video_complete", 100);
            }}
          >
            <source src={videoSrc} type="video/mp4" />
            <track
              kind="captions"
              src={captionsSrc}
              srcLang="en"
              label="English"
            />
          </video>

          {!started && (
            <button
              type="button"
              onClick={play}
              aria-label={t("HomePage.videoPlay")}
              className="group absolute inset-0 flex cursor-pointer items-center justify-center bg-black/10 transition-colors duration-200 hover:bg-black/0 focus-visible:outline-none"
            >
              <span className="flex h-20 w-20 items-center justify-center rounded-full bg-white text-indigo-600 shadow-2xl shadow-black/60 ring-8 ring-white/15 transition-transform duration-200 group-hover:scale-105 group-focus-visible:ring-indigo-400 motion-reduce:transition-none sm:h-24 sm:w-24">
                <FiPlay className="ml-1 h-9 w-9 sm:h-10 sm:w-10" />
              </span>
              <span className="absolute bottom-3 right-3 rounded-md bg-black/75 px-2 py-1 font-mono text-xs text-white sm:bottom-4 sm:right-4 sm:text-sm">
                {DURATION_LABEL}
              </span>
            </button>
          )}

          {/* The end card's button, made real: the video draws it, and this
              sits exactly on top to take the click — no second button under
              the frame. Ringed once the video has ended on it. */}
          {started && (onCta || ended) && (
            <button
              type="button"
              onClick={onStart}
              disabled={loading}
              aria-busy={loading || undefined}
              aria-label={startLabel}
              style={ctaBox}
              className="group absolute cursor-pointer focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-indigo-300 disabled:cursor-wait"
            >
              <span
                aria-hidden="true"
                className="absolute inset-0 rounded-[inherit] transition-colors duration-150 group-hover:bg-white/10 group-active:bg-black/15 motion-reduce:transition-none"
              />
              {ended && !loading && (
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute -inset-1.5 rounded-[inherit] ring-2 ring-indigo-300/70 motion-safe:animate-pulse"
                />
              )}
              {loading && (
                <span className="absolute inset-0 flex items-center justify-center rounded-[inherit] bg-indigo-600/90 text-white">
                  <Spinner size={24} />
                </span>
              )}
            </button>
          )}
        </div>
      </div>
    </section>
  );
};
